import {
  HMO_ROLE,
  isHmoAdmin,
  isHmoAuthorityRole,
  normalizeHmoRoleCode,
} from "../../../utils/hmoRoles";
import { HOSPITAL_QUERY_STAGE_LABELS } from "./patientDetailsFigmaConfig";
export type HmoHardcodedStageKey =
  | "emergency"
  | "referralAcceptance"
  | "discharge"
  | "claim"
  | "settlement";

export type AuthorityHardcodedStageKey =
  | HmoHardcodedStageKey
  | "billAudit"
  | "cpd"
  | "expert"
  | "approver"
  | "auditor"
  | "cmd";

/** Toggle Bill Audit stage in authority patient details (hidden until enabled). */
export const BILL_AUDIT_STAGE_VISIBLE = false;

export type AuthorityWorkflowStageKey = Extract<
  AuthorityHardcodedStageKey,
  "cpd" | "expert" | "approver" | "auditor" | "cmd"
>;

export type HmoHardcodedStage = {
  id: string;
  key: HmoHardcodedStageKey;
  label: string;
};

export type AuthorityHardcodedStage = {
  id: string;
  key: AuthorityHardcodedStageKey;
  label: string;
};

export const HMO_HARDCODED_STAGES: HmoHardcodedStage[] = [
  { id: "0", key: "emergency", label: "EMERGENCY" },
  { id: "1", key: "referralAcceptance", label: "REFERRAL ACCEPTANCE" },
  { id: "2", key: "discharge", label: "DISCHARGE" },
  { id: "3", key: "claim", label: "CLAIM" },
  { id: "4", key: "settlement", label: "SETTLEMENT" },
];

/** Non-hospital (authority) patient details – Figma stages */
export const AUTHORITY_HARDCODED_STAGES: AuthorityHardcodedStage[] = [
  { id: "0", key: "emergency", label: "EMERGENCY" },
  { id: "1", key: "referralAcceptance", label: "REFERRALS ACCEPTANCE" },
  { id: "2", key: "discharge", label: "DISCHARGE" },
  { id: "3", key: "claim", label: "CLAIM" },
  { id: "4", key: "billAudit", label: "BILL AUDIT" },
  { id: "5", key: "cpd", label: "CPD" },
  { id: "7", key: "approver", label: "APPROVER" },
  { id: "6", key: "expert", label: "EXPERT" },
  { id: "8", key: "auditor", label: "AUDITOR" },
  { id: "9", key: "cmd", label: "CMO" },
  { id: "10", key: "settlement", label: "SETTLEMENT" },
];

/** Authority visit-details stages visible to Referral Issuer (HMO_RI). */
export const HMO_RI_AUTHORITY_STAGE_KEYS: AuthorityHardcodedStageKey[] = [
  "emergency",
  "referralAcceptance",
];

export const isOpVisitType = (visitType?: string | null): boolean => {
  const normalized = String(visitType || "")
    .trim()
    .toUpperCase();
  return normalized === "OP" || normalized.includes("OPD");
};

export const isEmergencyVisitType = (visitType?: string | null): boolean =>
  String(visitType || "")
    .trim()
    .toUpperCase()
    .includes("EMERGENCY");

/**
 * The Emergency stage panel only applies to emergency visits, and an emergency case reaches the
 * IP pipeline through the Emergency stage itself, so Referral Acceptance never applies there.
 */
export const filterStagesForEmergencyVisit = <T extends { key: string }>(
  stages: T[],
  visitType?: string | null,
): T[] =>
  isEmergencyVisitType(visitType)
    ? stages.filter((stage) => stage.key !== "referralAcceptance")
    : stages.filter((stage) => stage.key !== "emergency");

/** Same swap for the query sidebar: Emergency panel in, Referral Acceptance panel out. */
export const filterQueryStagesForEmergencyVisit = (
  stages: Array<Record<string, any>>,
  visitType?: string | null,
): Array<Record<string, any>> => {
  const drop = isEmergencyVisitType(visitType) ? "REFERRAL" : "EMERGENCY";
  return stages.filter(
    (stage) =>
      !String(stage?.visitStage || stage?.displayLabel || "")
        .toUpperCase()
        .includes(drop),
  );
};

const OP_HIDDEN_HEADER_FIELDS = new Set(["ward", "dod"]);

export const filterHeaderFieldsForOpVisit = <T extends string>(
  fields: readonly T[],
  visitType?: string | null,
): T[] =>
  isOpVisitType(visitType)
    ? fields.filter((field) => !OP_HIDDEN_HEADER_FIELDS.has(field))
    : [...fields];

export const filterStagesForOpVisit = <T extends { key: string }>(
  stages: T[],
  visitType?: string | null,
): T[] =>
  isOpVisitType(visitType)
    ? stages.filter((stage) => stage.key !== "discharge")
    : stages;

export const filterQueryStagesForOpVisit = (
  stages: Array<Record<string, any>>,
  visitType?: string | null,
): Array<Record<string, any>> =>
  isOpVisitType(visitType)
    ? stages.filter((stage) => {
        const label = String(
          stage?.visitStage || stage?.displayLabel || "",
        ).toUpperCase();
        return !label.includes("DISCHARGE") &&
          !label.includes("ENHANCEMENT") &&
          !label.includes("LIVE UPDATE");
      })
    : stages;

export const AUTHORITY_WORKFLOW_STAGE_KEYS = new Set<AuthorityHardcodedStageKey>([
  "cpd",
  "expert",
  "approver",
  "auditor",
  "cmd",
]);

/** Clinical stages always shown to non-RI HMO authority users in patient details. */
export const HMO_AUTHORITY_BASE_STAGE_KEYS: AuthorityHardcodedStageKey[] = [
  "emergency",
  "referralAcceptance",
  "discharge",
  "claim",
  "settlement",
];

export const filterAuthorityHardcodedStagesForRole = (
  stages: AuthorityHardcodedStage[],
  role?: string | null,
  claimSubmitted = false,
): AuthorityHardcodedStage[] => {
  const normalizedRole = normalizeHmoRoleCode(role);
  let visible = stages;

  if (normalizedRole === HMO_ROLE.RI) {
    const allowed = new Set(HMO_RI_AUTHORITY_STAGE_KEYS);
    visible = visible.filter((stage) => allowed.has(stage.key));
  }

  const isNonRiHmoAuthority =
    isHmoAuthorityRole(role) && normalizedRole !== HMO_ROLE.RI;
  const showWorkflowStages =
    claimSubmitted || isHmoAdmin(role) || isNonRiHmoAuthority;

  if (!showWorkflowStages) {
    visible = visible.filter((stage) => !AUTHORITY_WORKFLOW_STAGE_KEYS.has(stage.key));
  }

  if (!BILL_AUDIT_STAGE_VISIBLE) {
    visible = visible.filter((stage) => stage.key !== "billAudit");
  }

  if (normalizedRole !== HMO_ROLE.RI && visible.length === 0) {
    const allowed = new Set(HMO_AUTHORITY_BASE_STAGE_KEYS);
    visible = stages.filter((stage) => allowed.has(stage.key));
  }

  return visible;
};

export const filterAuthorityQueryVisitStagesForRole = (
  stages: Array<Record<string, any>>,
  role?: string | null,
): Array<Record<string, any>> => {
  if (isHmoAdmin(role)) return stages;
  if (normalizeHmoRoleCode(role) !== HMO_ROLE.RI) return stages;
  return stages.filter((stage) => {
    const label = String(
      stage?.visitStage || stage?.displayLabel || "",
    ).toUpperCase();
    return label.includes("REFERRAL");
  });
};

export const AUTHORITY_QUERY_STAGE_LABELS = [
  "EMERGENCY",
  "REFERRALS ACCEPTANCE",
  "ENHANCEMENT",
  "DISCHARGE",
  "CLAIM",
  "SETTLEMENT",
] as const;

/** Workflow review stages whose NMI queries are grouped under the CLAIM query panel. */
export const WORKFLOW_NMI_QUERY_STAGE_KEYS = new Set([
  "CPD",
  "EXPERT",
  "EXPERT",
  "APPROVER",
  "AUDITOR",
  "CMD",
  "CMO",
]);

export const isWorkflowStageNmiQuery = (queryStage?: string | null): boolean =>
  WORKFLOW_NMI_QUERY_STAGE_KEYS.has(normalizeStageName(queryStage || ""));

const panelStageIsClaim = (
  panelStage: Record<string, any> | null | undefined,
): boolean => {
  if (!panelStage) return false;
  const candidates = [panelStage?.visitStage, panelStage?.displayLabel].filter(
    Boolean,
  ) as string[];
  return candidates.some((candidate) => {
    const normalized = normalizeStageName(candidate);
    return normalized === "CLAIM" || normalized.includes("CLAIM");
  });
};

export const AUTHORITY_PATIENT_DETAIL_STAGES = [
  { apiName: "Emergency", label: "EMERGENCY" },
  { apiName: "ClaimId Generation", label: "REFERRALS ACCEPTANCE" },
  { apiName: "Enhancement", label: "ENHANCEMENT" },
  { apiName: "DISCHARGE", label: "DISCHARGE" },
  { apiName: "CLAIM", label: "CLAIM" },
  { apiName: "SETTLEMENT", label: "SETTLEMENT" },
] as const;

export const HMO_PATIENT_DETAIL_STAGES = [
  { apiName: "Emergency", label: "EMERGENCY" },
  { apiName: "ClaimId Generation", label: "REFERRAL ACCEPTANCE" },
  { apiName: "Enhancement", label: "ENHANCEMENT" },
  { apiName: "DISCHARGE", label: "DISCHARGE" },
  { apiName: "CLAIM", label: "CLAIM" },
  { apiName: "SETTLEMENT", label: "SETTLEMENT" },
] as const;

export type HmoPatientDetailStageApiName =
  (typeof HMO_PATIENT_DETAIL_STAGES)[number]["apiName"];

export const normalizeStageName = (name: string) =>
  String(name || "")
    .toUpperCase()
    .replace(/\s+/g, "");

export const matchesApiStage = (visitStage: string, apiName: string) => {
  const a = normalizeStageName(visitStage);
  const b = normalizeStageName(apiName);
  return a === b || a.includes(b) || b.includes(a);
};

/** Merge API visit stages with fixed HMO order + Figma labels */
export const getHmoOrderedStages = (
  visitStages?: Array<Record<string, any>> | null,
) => {
  if (!visitStages?.length) return [];

  return HMO_PATIENT_DETAIL_STAGES.map(({ apiName, label }) => {
    const match = visitStages.find((s) =>
      matchesApiStage(String(s?.visitStage || ""), apiName),
    );
    if (!match) return null;
    return { ...match, displayLabel: label };
  }).filter(Boolean) as Array<
    Record<string, any> & { displayLabel: string }
  >;
};

/** Visit stages for hospital query sidebar – real IDs with Figma display names */
export const getHmoQueryVisitStages = (
  visitStages?: Array<Record<string, any>> | null,
) => {
  const ordered = getHmoOrderedStages(visitStages);
  if (ordered.length) {
    return ordered.map((stage) => ({
      ...stage,
      visitStage: stage.displayLabel,
    }));
  }

  return (visitStages ?? []).filter(
    (stage) =>
      !["RESUBMISSION"].includes(String(stage?.visitStage || "").toUpperCase()),
  );
};

/** Visit stages for authority query sidebar */
export const getAuthorityQueryVisitStages = (
  visitStages?: Array<Record<string, any>> | null,
) => {
  if (!visitStages?.length) return [];

  return AUTHORITY_PATIENT_DETAIL_STAGES.map(({ apiName, label }) => {
    const match = visitStages.find((s) =>
      matchesApiStage(String(s?.visitStage || ""), apiName),
    );
    if (!match) return null;
    return { ...match, visitStage: label };
  }).filter(Boolean) as Array<Record<string, any>>;
};

/** Map hardcoded stage keys → API visit stage names */
export const HARDCODED_STAGE_API_NAMES: Record<string, string> = {
  emergency: "Emergency",
  referralAcceptance: "ClaimId Generation",
  discharge: "DISCHARGE",
  claim: "CLAIM",
  settlement: "SETTLEMENT",
};

export const resolveApiStageForHardcodedKey = (
  stageKey: string,
  visitStages?: Array<Record<string, any>> | null,
) => {
  const apiName = HARDCODED_STAGE_API_NAMES[stageKey];
  if (!apiName || !visitStages?.length) return null;
  return (
    visitStages.find((s) =>
      matchesApiStage(String(s?.visitStage || ""), apiName),
    ) || null
  );
};

/** Normalized key for a QUERY sidebar panel (e.g. DISCHARGE, CLAIM). */
export const getQueryPanelStageKey = (
  panelStage: Record<string, any> | null | undefined,
): string => {
  if (!panelStage) return "";
  const label = String(
    panelStage.visitStage || panelStage.displayLabel || "",
  ).trim();
  return normalizeStageName(label);
};

/** query_stage value to persist when raising a query from a sidebar panel. */
export const resolveQueryStageForPanel = (
  panelStage: Record<string, any> | null | undefined,
): string => {
  if (!panelStage) return "";
  const panelLabel = String(
    panelStage.visitStage || panelStage.displayLabel || "",
  ).trim();
  if (!panelLabel) return "";

  const stageDef = [
    ...HMO_PATIENT_DETAIL_STAGES,
    ...AUTHORITY_PATIENT_DETAIL_STAGES,
  ].find(
    ({ apiName, label }) =>
      matchesApiStage(panelLabel, apiName) || matchesApiStage(panelLabel, label),
  );
  if (stageDef) {
    return stageDef.label;
  }

  return panelLabel;
};

/** Whether a saved query belongs under a sidebar panel, matched by query_stage string. */
export const queryMatchesPanelStage = (
  queryStage: string | undefined | null,
  panelStage: Record<string, any> | null | undefined,
): boolean => {
  if (!queryStage || !panelStage) return false;

  if (isWorkflowStageNmiQuery(queryStage) && panelStageIsClaim(panelStage)) {
    return true;
  }

  const queryNorm = normalizeStageName(queryStage);
  const panelCandidates = [panelStage?.visitStage, panelStage?.displayLabel].filter(
    Boolean,
  ) as string[];

  for (const candidate of panelCandidates) {
    const panelNorm = normalizeStageName(candidate);
    if (
      queryNorm === panelNorm ||
      queryNorm.includes(panelNorm) ||
      panelNorm.includes(queryNorm)
    ) {
      return true;
    }
    if (
      matchesApiStage(queryStage, candidate) ||
      matchesApiStage(candidate, queryStage)
    ) {
      return true;
    }
  }

  const allStageDefs = [
    ...HMO_PATIENT_DETAIL_STAGES,
    ...AUTHORITY_PATIENT_DETAIL_STAGES,
  ];
  const queryMatchesDef = allStageDefs.some(
    ({ apiName, label }) =>
      matchesApiStage(queryStage, apiName) || matchesApiStage(queryStage, label),
  );
  if (!queryMatchesDef) return false;

  return allStageDefs.some(({ apiName, label }) => {
    const queryOnDef =
      matchesApiStage(queryStage, apiName) || matchesApiStage(queryStage, label);
    const panelOnDef = panelCandidates.some(
      (candidate) =>
        matchesApiStage(candidate, apiName) || matchesApiStage(candidate, label),
    );
    return queryOnDef && panelOnDef;
  });
};

/** Map query.queryStage to the visit stage row used in the QUERY sidebar. */
export const resolveVisitStageIdForQuery = (
  queryStage: string,
  visitStages?: Array<Record<string, any>> | null,
): string | null => {
  if (!queryStage || !visitStages?.length) return null;

  const allStageDefs = [
    ...HMO_PATIENT_DETAIL_STAGES,
    ...AUTHORITY_PATIENT_DETAIL_STAGES,
  ];
  const stageDef = allStageDefs.find(
    ({ apiName, label }) =>
      matchesApiStage(queryStage, apiName) || matchesApiStage(queryStage, label),
  );

  if (stageDef) {
    const defMatch = visitStages.find(
      (stage) =>
        matchesApiStage(String(stage?.visitStage || ""), stageDef.apiName) ||
        matchesApiStage(String(stage?.displayLabel || ""), stageDef.label) ||
        normalizeStageName(stage?.visitStage) === normalizeStageName(stageDef.label),
    );
    if (defMatch?.visitStageId != null) {
      return String(defMatch.visitStageId);
    }
  }

  const normalizedQueryStage = normalizeStageName(queryStage);
  const match = visitStages.find((stage) => {
    const candidates = [stage?.visitStage, stage?.displayLabel].filter(
      Boolean,
    ) as string[];

    return candidates.some((candidate) => {
      const normalizedCandidate = normalizeStageName(candidate);
      return (
        normalizedCandidate === normalizedQueryStage ||
        normalizedCandidate.includes(normalizedQueryStage) ||
        normalizedQueryStage.includes(normalizedCandidate) ||
        matchesApiStage(candidate, queryStage) ||
        matchesApiStage(queryStage, candidate)
      );
    });
  });

  return match?.visitStageId != null ? String(match.visitStageId) : null;
};

export const getQueryVisitStageId = (
  stage: Record<string, any> | null | undefined,
): string => {
  const id = stage?.visitStageId;
  return id != null ? String(id) : "";
};

/** Map main-form active stage id to a QUERY sidebar panel id (when it matches). */
export const resolveQuerySidebarExpandedStageId = (
  activeKey: string | number | undefined | null,
  visitStages?: Array<Record<string, any>> | null,
): string | null => {
  if (activeKey == null || activeKey === "" || !visitStages?.length) {
    return null;
  }
  const match = visitStages.find(
    (stage) => String(stage?.visitStageId) === String(activeKey),
  );
  return match?.visitStageId != null ? String(match.visitStageId) : null;
};

export const getHospitalQueryVisitStagesWithFallback = (
  visitStages?: Array<Record<string, any>> | null,
) => {
  const mapped = getHmoQueryVisitStages(visitStages);
  if (mapped.length) return mapped;

  return HOSPITAL_QUERY_STAGE_LABELS.map((label, index) => ({
    visitStageId: index + 1,
    visitStage: label,
    stageLevelId: index + 1,
  }));
};

/** Fallback synthetic stages for authority query sidebar when API has no match */
export const getAuthorityQueryVisitStagesWithFallback = (
  visitStages?: Array<Record<string, any>> | null,
) => {
  const mapped = getAuthorityQueryVisitStages(visitStages);
  if (mapped.length) return mapped;

  return AUTHORITY_QUERY_STAGE_LABELS.map((label, index) => ({
    visitStageId: index + 1,
    visitStage: label,
    stageLevelId: index + 1,
  }));
};
